import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';

type Entitlements = {
  plan: 'free' | 'basic' | 'full' | 'unrestricted' | string;
  unrestricted: boolean;
  expires_at: string | null;
  seats_limit: number | null;
  seats_used: number | null;
  owner_user_id: number | null;
  covered_by_parent: boolean;
  features: Record<string, boolean>;
};

type MyChildrenResponse = {
  subscription: {
    plan: string | null;
    expires_at: string | null;
    child_seats_limit: number | null;
    child_seats_used: number;
  };
  children: Array<{ id: number; full_name: string; email: string; over16: boolean; covered: boolean }>;
};

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const { me, uiRole, signOut } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const user = useMemo(() => {
    return {
      role: (uiRole || 'student') as 'admin' | 'teacher' | 'student' | 'parent',
      name: me?.full_name || 'Utilizator',
      avatar: me?.avatar_url || undefined,
      is_platform_admin: Boolean((me as any)?.is_platform_admin),
    };
  }, [me?.avatar_url, me?.full_name, uiRole, me]);

  const showSidebar = user.role === 'admin' || user.role === 'teacher';

  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [children, setChildren] = useState<MyChildrenResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState<number | null>(null);
  const [billingLoading, setBillingLoading] = useState<'basic' | 'full' | 'portal' | null>(null);
  const [stripeResult, setStripeResult] = useState<'success' | 'cancel' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entResp, childrenResp] = await Promise.all([
        api.get('/api/subscriptions/me'),
        user.role === 'parent' ? api.get('/api/subscriptions/my-children') : Promise.resolve({ data: null } as any),
      ]);
      setEnt(entResp.data as any);
      setChildren(childrenResp.data as any);
    } finally {
      setLoading(false);
    }
  }, [user.role]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('stripe');
    const sessionId = params.get('session_id');
    if (status === 'success' || status === 'cancel') {
      setStripeResult(status);
      (async () => {
        if (status === 'success' && sessionId) {
          try {
            await api.post('/api/billing/checkout-success', { session_id: sessionId });
          } catch (e: any) {
            alert(e?.response?.data?.detail || 'Nu am putut confirma abonamentul. Apasă Reîncarcă.');
          }
        }
        params.delete('stripe');
        params.delete('session_id');
        const qs = params.toString();
        navigate(`/subscriptions${qs ? `?${qs}` : ''}`, { replace: true });
        if (status === 'success') {
          await load();
        }
      })();
    }
  }, [navigate, load]);

  const linkChild = useCallback(
    async (childId: number) => {
      setLinking(childId);
      try {
        await api.post('/api/subscriptions/link-child', { child_user_id: childId });
        await load();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut atașa copilul.');
      } finally {
        setLinking(null);
      }
    },
    [load]
  );

  const unlinkChild = useCallback(
    async (childId: number) => {
      setLinking(childId);
      try {
        await api.post('/api/subscriptions/unlink-child', { child_user_id: childId });
        await load();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut detașa copilul.');
      } finally {
        setLinking(null);
      }
    },
    [load]
  );

  const planCard = (title: string, price: string, bullets: string[], active: boolean, cta?: React.ReactNode) => (
    <div className={`bg-card border rounded-lg p-5 ${active ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-foreground">{title}</div>
          <div className="text-sm text-muted-foreground">{price}</div>
        </div>
        {active ? <span className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground">Activ</span> : null}
      </div>
      <ul className="mt-3 space-y-2 text-sm text-foreground">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-success mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {cta ? <div className="mt-4">{cta}</div> : null}
    </div>
  );

  const isNativeApp = useMemo(() => {
    try {
      const w: any = typeof window !== 'undefined' ? window : undefined;
      if (!w) return false;
      if (w.Capacitor?.isNativePlatform?.()) return true;
      const protocol = String(w.location?.protocol || '');
      return protocol === 'capacitor:' || protocol === 'file:';
    } catch {
      return false;
    }
  }, []);

  const webSubscriptionsUrl = useMemo(() => {
    const base = String((import.meta as any)?.env?.VITE_WEB_APP_URL || '')
      .trim()
      .replace(/\/+$/, '');
    if (!base) return '';
    return `${base}/subscriptions`;
  }, []);

  const currentPlan = (ent?.plan || 'free').toLowerCase();
  const childSeatsLimit = children?.subscription?.child_seats_limit ?? null;
  const childSeatsUsed = children?.subscription?.child_seats_used ?? 0;
  const childSeatsUnlimited = childSeatsLimit === -1;
  const childSeatsFull = childSeatsLimit != null && !childSeatsUnlimited && childSeatsUsed >= childSeatsLimit;

  const startCheckout = useCallback(async (plan: 'basic' | 'full') => {
    setBillingLoading(plan);
    try {
      const resp = await api.post('/api/billing/checkout-session', { plan, return_url_base: window.location.origin });
      const url: string | undefined = resp.data?.url;
      if (!url) {
        alert('Nu am putut iniția plata (răspuns invalid).');
        return;
      }
      window.location.href = url;
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut iniția plata.');
    } finally {
      setBillingLoading(null);
    }
  }, []);

  const openPortal = useCallback(async () => {
    setBillingLoading('portal');
    try {
      const resp = await api.post('/api/billing/portal-session', {});
      const url: string | undefined = resp.data?.url;
      if (!url) {
        alert('Nu am putut deschide portalul (răspuns invalid).');
        return;
      }
      window.location.href = url;
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut deschide portalul.');
    } finally {
      setBillingLoading(null);
    }
  }, []);

  const billingCta = useCallback(
    (plan: 'basic' | 'full') => {
      if (isNativeApp) {
        return (
          <div className="space-y-2">
            <Button variant="outline" disabled>
              Plata doar pe web
            </Button>
            {webSubscriptionsUrl ? (
              <a className="text-xs text-primary hover:underline inline-block" href={webSubscriptionsUrl} target="_blank" rel="noreferrer">
                Deschide pagina de abonamente
              </a>
            ) : (
              <div className="text-xs text-muted-foreground">Deschide platforma din browser pentru plată.</div>
            )}
          </div>
        );
      }

      if (ent?.unrestricted) {
        return (
          <Button variant="outline" disabled>
            Activ prin licență
          </Button>
        );
      }

      if (currentPlan === plan) {
        return (
          <Button variant="outline" onClick={() => void openPortal()} disabled={billingLoading === 'portal'}>
            Gestionează
          </Button>
        );
      }

      return (
        <Button variant="default" onClick={() => void startCheckout(plan)} disabled={Boolean(billingLoading)}>
          {billingLoading === plan ? 'Se deschide...' : plan === 'basic' ? 'Alege Basic' : 'Alege Full'}
        </Button>
      );
    },
    [billingLoading, currentPlan, ent?.unrestricted, isNativeApp, openPortal, startCheckout, webSubscriptionsUrl]
  );

  return (
    <>
      <Header user={user} onNavigate={(p) => navigate(p)} onSignOut={() => signOut()} />

      <div className="min-h-screen bg-background pt-16">
        {showSidebar ? (
          <DashboardSidebar
            user={{ role: user.role as any, is_platform_admin: Boolean((me as any)?.is_platform_admin) }}
            isCollapsed={isSidebarCollapsed}
            onNavigate={(path) => navigate(path)}
          />
        ) : null}

        <main className={`transition-all duration-300 ${showSidebar ? (isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64') : ''}`}>
          <div className="p-6 max-w-6xl mx-auto space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-2xl font-bold text-foreground">Abonamente</div>
                <div className="text-sm text-muted-foreground">Gestionează accesul la funcționalități.</div>
              </div>
              <div className="flex items-center gap-2">
                {!isNativeApp && !ent?.unrestricted && (currentPlan === 'basic' || currentPlan === 'full') ? (
                  <Button
                    variant="outline"
                    iconName="ExternalLink"
                    onClick={() => void openPortal()}
                    disabled={billingLoading === 'portal'}
                  >
                    Gestionează abonamentul
                  </Button>
                ) : null}
                <Button variant="outline" iconName="RefreshCw" onClick={() => void load()} disabled={loading}>
                  Reîncarcă
                </Button>
              </div>
            </div>

            {stripeResult ? (
              <div
                className={`border rounded-lg p-4 ${
                  stripeResult === 'success'
                    ? 'bg-success/10 border-success/30 text-foreground'
                    : 'bg-warning/10 border-warning/30 text-foreground'
                }`}
              >
                <div className="flex items-start gap-2">
                  <Icon name={stripeResult === 'success' ? 'CheckCircle2' : 'AlertTriangle'} size={18} className="mt-0.5" />
                  <div className="space-y-1">
                    <div className="font-semibold">{stripeResult === 'success' ? 'Plată confirmată' : 'Plată anulată'}</div>
                    <div className="text-sm text-muted-foreground">
                      {stripeResult === 'success'
                        ? 'Abonamentul se actualizează automat. Dacă nu vezi schimbarea imediat, apasă Reîncarcă.'
                        : 'Nu s-a efectuat nicio plată. Poți încerca din nou oricând.'}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {ent?.unrestricted ? (
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="BadgeCheck" size={18} className="text-primary mt-0.5" />
                  <div>
                    <div className="font-semibold text-foreground">Licență activă (fără restricții)</div>
                    <div className="text-sm text-muted-foreground">Acces complet acordat de administratorul platformei/școlii.</div>
                  </div>
                </div>
              </div>
            ) : null}

            {isNativeApp ? (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <div className="font-semibold text-foreground">Plata abonamentelor se face doar pe web</div>
                    <div className="text-sm text-muted-foreground">
                      Din aplicație poți folosi abonamentul, dar achiziția și gestionarea se fac din browser.
                      {webSubscriptionsUrl ? (
                        <>
                          {' '}
                          <a className="text-primary hover:underline" href={webSubscriptionsUrl} target="_blank" rel="noreferrer">
                            Deschide pagina de abonamente
                          </a>
                          .
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {planCard(
                'Free',
                '0€ / lună',
                [
                  'Catalog: vizualizare note și absențe',
                  'Mesaje: citire + 5 mesaje/zi',
                  'Comunitate: vizualizare + reacții (like/inimă)',
                  user.role === 'parent' ? 'Notificări (doar părinți): note + absențe' : 'Fără notificări',
                ],
                currentPlan === 'free',
                <Button variant="outline" disabled>
                  Plan curent
                </Button>
              )}

              {planCard(
                'Basic',
                '3€ / lună',
                [
                  'Mesaje: fără limită',
                  'Comentarii în comunitate',
                  'Calendar: poți crea programări',
                  'Descărcare cursuri și teme',
                  'Încărcare teme rezolvate',
                  'Atașare 1 cont copil (<16) la abonament',
                ],
                currentPlan === 'basic',
                billingCta('basic')
              )}

              {planCard(
                'Full',
                '5€ / lună',
                ['Tot din Basic', 'Postări în comunitate', 'Descărcare resurse din Bibliotecă', 'Atașare conturi copil (<16) nelimitat'],
                currentPlan === 'full',
                billingCta('full')
              )}
            </div>

            <div className="text-sm text-muted-foreground">
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <div>
                      Abonamentele B2C se aplică elevilor <strong>peste 16 ani</strong> și părinților elevilor <strong>peste 16 ani</strong>.
                      Pentru elevii <strong>sub 16 ani</strong>, abonamentul este gestionat de părinte, iar accesul copilului se acordă prin
                      atașarea contului la abonamentul părintelui (seats).
                    </div>
                    <div>
                      Pentru școli (B2B), pachetele sunt <strong>personalizabile</strong> (licențe la nivel de școală/clasă/utilizator). Pentru
                      ofertă și activare, scrie-ne la{' '}
                      <a className="text-primary hover:underline" href="mailto:office@globallinknet.ro">
                        office@globallinknet.ro
                      </a>
                      .
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {ent?.expires_at ? (
              <div className="text-xs text-muted-foreground">
                Expiră la: {new Date(ent.expires_at).toLocaleString('ro-RO')}
                {ent.covered_by_parent ? ' (acoperit de abonamentul părintelui)' : ''}
              </div>
            ) : null}

            {user.role === 'parent' ? (
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <div className="font-semibold text-foreground">Copii (sub 16)</div>
                    <div className="text-sm text-muted-foreground">
                      Atașează copiii la abonamentul tău (seats) ca să poată avea acces Basic/Full.
                    </div>
                    {childSeatsFull ? (
                      <div className="text-xs text-muted-foreground mt-1">
                        Pe abonamentul <strong>Basic</strong> poți atașa <strong>1</strong> copil. Pentru mai mulți, treci la{' '}
                        <strong>Full</strong>.
                      </div>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {children?.subscription?.child_seats_limit == null
                      ? ''
                      : `Seats: ${children?.subscription?.child_seats_used ?? 0} / ${
                          children?.subscription?.child_seats_limit === -1 ? '∞' : children?.subscription?.child_seats_limit
                        }`}
                  </div>
                </div>

                {loading ? (
                  <div className="text-sm text-muted-foreground">Se încarcă...</div>
                ) : (children?.children || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nu există copii asociați.</div>
                ) : (
                  <ul className="divide-y divide-border">
                    {(children?.children || []).map((c) => (
                      <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground">{c.full_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {c.over16 ? <span className="text-xs text-muted-foreground">≥16</span> : null}
                          {c.covered ? (
                            <>
                              <span className="text-xs px-2 py-1 rounded bg-muted text-foreground">Atașat</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (!window.confirm('Detașezi acest copil de pe abonament?')) return;
                                  void unlinkChild(c.id);
                                }}
                                disabled={linking === c.id}
                              >
                                Detașează
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void linkChild(c.id)}
                              disabled={linking === c.id || Boolean(childSeatsFull)}
                              title={childSeatsFull ? 'Ai atins limita de copii pe abonamentul curent.' : undefined}
                            >
                              Atașează
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </>
  );
}
